import React, { useContext, useEffect, useState } from "react"; // Ensure useContext is imported
import { useCart } from "../../contexts/CartContext";
import { ThemeContext } from "../../contexts/ThemeContext"; // Import ThemeContext
import { FaTrash } from "react-icons/fa";
import { FaMinus, FaPlus } from "react-icons/fa";
import { PlusIcon, MinusIcon, Trash2Icon } from "lucide-react";
import axios from "axios"; // Ensure axios is imported
import { BASE_URL, API_TOKEN, SLINK } from "../../config";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useBreadcrumb } from "../../contexts/Breadcrums"; // Import breadcrumb context
import nocart from "../CartItems/nocarts.png";

const CartItems = () => {
  const {
    cartProducts,
    setCartProducts,
    removeFromCart,
    updateCartItemQuantity,
    currency,
  } = useCart(); // Use updateCartItemQuantity from context
  const { isDarkMode } = useContext(ThemeContext); // Get dark mode state
  const [error, setError] = useState(null); // Added error state
  const [isLoading, setIsLoading] = useState(true); // Local loading state
  const deviceId = localStorage.getItem("device_id"); // Get device ID
  const navigate = useNavigate(); // Initialize navigate

  const fetchCartProducts = async () => {
    // Added fetchCartProducts function
    setIsLoading(true);

    try {
      const response = await axios.get(
        `${BASE_URL}/customer/show-cart-products?device_id=${deviceId}&slink=${SLINK}`
      );
      console.log(response);

      if (
        response.data.productDetails &&
        Array.isArray(response.data.productDetails)
      ) {
        setCartProducts(response.data.productDetails);
      } else {
        setError("Unexpected response format");
      }
    } catch (error) {
      console.error("Error fetching cart products:", error);
      setError("Failed to fetch cart products");
    } finally {
      setIsLoading(false);
    }
  };

  const incrementQuantity = (id) => {
    const item = cartProducts.find((product) => product.cart_product_id === id);
    if (item) {
      const newQuantity = item.qty + 1;
      updateCartItemQuantity(id, newQuantity); // Call the API to update quantity
      setCartProducts((prev) =>
        prev.map((product) =>
          product.cart_product_id === id
            ? { ...product, qty: newQuantity }
            : product
        )
      ); // Update local state immediately
    }
  };

  const decrementQuantity = (id) => {
    const item = cartProducts.find((product) => product.cart_product_id === id);
    if (item && item.qty > 1) {
      const newQuantity = item.qty - 1;
      updateCartItemQuantity(id, newQuantity); // Call the API to update quantity
      setCartProducts((prev) =>
        prev.map((product) =>
          product.cart_product_id === id
            ? { ...product, qty: newQuantity }
            : product
        )
      ); // Update local state immediately
    }
  };

  const handleRemoveFromCart = (id) => {
    removeFromCart(id); // Call the API to remove item
    setCartProducts((prev) =>
      prev.filter((product) => product.cart_product_id !== id)
    ); // Update local state immediately
  };

  const handleCheckout = () => {
    navigate("/checkout"); // Navigate to checkout page
  };

  useEffect(() => {
    // Added useEffect to call fetchCartProducts
    fetchCartProducts();
  }, []);

  if (error) {
    // Added error handling
    return (
      <div
        className={`text-center mt-8 ${
          isDarkMode ? "text-red-500" : "text-red-700"
        }`}
      >
        {error}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={`flex justify-center items-center h-screen ${
          isDarkMode ? "bg-black text-white" : "bg-white text-black"
        }`}
      >
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (cartProducts.length === 0) {
    return (
      <div className="text-center mt-8 flex flex-col items-center justify-center bg-black">
        <img
          src={nocart}
          alt="Empty Cart"
          className="mx-auto mb-4 w-1/5 animate-pulse"
        />
        <p className="text-lg text-white">
          Your cart is currently empty. Start adding items!
        </p>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${
        isDarkMode ? "bg-black text-white" : "bg-white text-black"
      }`}
    >
      {/* Mobile Cart Interface */}
      <div className="block md:hidden">
        {/* Mobile-specific layout */}
        <div
          className={`rounded-lg p-4 mb-4 ${
            isDarkMode ? "bg-gray-800" : "bg-gray-200"
          }`}
        >
          <h2 className="text-xl font-bold mb-4">Cart Items</h2>
          {/* Mobile cart items rendering */}
          {cartProducts.map((item) => (
            <div key={item.cart_product_id} className="flex flex-col mb-4">
              {/* Cart Item Details */}
              <div className="flex items-center">
                <img
                  src={item.photo[0].url}
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded mr-4"
                />
                <div>
                  <p className="font-semibold">{item.name}</p>
                  <p className="text-sm text-gray-400">{item.description}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2">
                {/* Quantity Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => incrementQuantity(item.cart_product_id)}
                    className="bg-[#ffd700] text-black p-2 rounded-full"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                  <span className="mx-2 text-lg">{item.qty}</span>
                  <button
                    onClick={() => decrementQuantity(item.cart_product_id)}
                    className="bg-[#333333] p-2 rounded-full"
                  >
                    <MinusIcon className="h-4 w-4" />
                  </button>
                </div>
                <button
                  onClick={() => handleRemoveFromCart(item.cart_product_id)}
                  className="bg-[#3d2020] text-red-500 px-4 py-2 rounded-lg flex items-center ml-auto"
                >
                  <Trash2Icon className="h-5 w-5 mr-2" />
                  Delete
                </button>
              </div>
              <hr className="my-4" /> {/* Added horizontal rule */}
            </div>
          ))}
        </div>
        {/* Mobile order summary */}
        <div
          className={`rounded-lg p-4 ${
            isDarkMode ? "bg-gray-800" : "bg-gray-200"
          }`}
        >
          <h2 className="text-xl font-bold mb-4">Total price of products</h2>
          <p className="text-sm text-gray-400 mb-2">
            Number of Products (
            {cartProducts.reduce((acc, item) => acc + item.qty, 0)})
          </p>
          <p className="text-2xl font-bold mb-6">
            <span dangerouslySetInnerHTML={{ __html: currency }} />
            {cartProducts
              .reduce((acc, item) => acc + item.price * item.qty, 0)
              .toFixed(2)}{" "}
          </p>
          <button
            onClick={handleCheckout}
            className="w-full bg-[#ffd700] text-black py-3 rounded-lg font-bold text-lg mb-4 hover:bg-[#e5c100] transition duration-300"
          >
            Checkout
          </button>
          <button
            onClick={() => navigate("/swiss-bar")}
            className="w-full border border-[#ffd700] text-[#ffd700] py-3 rounded-lg font-bold text-lg hover:bg-[#ffd700] hover:text-black transition duration-300"
          >
            Continue Shopping
          </button>
        </div>
      </div>

      {/* Desktop Cart Interface */}
      <div className="hidden md:block">
        {/* Desktop-specific layout */}
        <div
          className={`flex space-x-6 ${isDarkMode ? "bg-black" : "bg-white"}`}
        >
          {/* Desktop cart items rendering */}
          <div
            className={`flex-grow rounded-lg p-6 ${
              isDarkMode ? "bg-gray-800" : "bg-gray-200"
            }`}
          >
            <h2 className="text-2xl font-bold mb-6">Cart Items</h2>
            <div
              className={`rounded-lg overflow-hidden ${
                isDarkMode ? "bg-gray-700" : "bg-gray-300"
              }`}
            >
              <table className="w-full">
                <thead>
                  <tr
                    className={`${
                      isDarkMode ? "bg-gray-600" : "bg-gray-200"
                    } text-sm`}
                  >
                    <th className="text-left py-3 px-4">Product</th>
                    <th className="text-left py-3 px-4">Unit Price</th>
                    <th className="text-left py-3 px-4">Quantity</th>
                    <th className="text-left py-3 px-4">Total</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {cartProducts.map((item) => (
                    <>
                      <tr
                        className="border-b border-[#333333]"
                        key={item.cart_product_id}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-4 ">
                            <img
                              src={item.photo[0].url}
                              alt={item.name}
                              className="w-20 h-20 object-cover rounded"
                            />
                            <div>
                              <p className="font-semibold">{item.name}</p>
                              <p className="text-sm text-gray-400">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {" "}
                          <span
                            dangerouslySetInnerHTML={{ __html: currency }}
                          />
                          {item.price ? item.price : "0.00"}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                incrementQuantity(item.cart_product_id)
                              }
                              className="bg-[#ffd700] text-black p-1 rounded"
                            >
                              <FaPlus className="h-4 w-4" />
                            </button>
                            <span className="mx-2 text-lg">{item.qty}</span>
                            <button
                              onClick={() =>
                                decrementQuantity(item.cart_product_id)
                              }
                              className="bg-[#333333] p-1 rounded"
                            >
                              <FaMinus className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="py-4 px-4 font-semibold">
                          <span
                            dangerouslySetInnerHTML={{ __html: currency }}
                          />
                          {(item.price * item.qty).toFixed(2)}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            onClick={() =>
                              handleRemoveFromCart(item.cart_product_id)
                            }
                            className="text-red-500"
                          >
                            <FaTrash className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Desktop order summary */}
          <div
            className={`w-80 rounded-lg p-6 h-fit ${
              isDarkMode ? "bg-gray-800" : "bg-gray-200"
            }`}
          >
            <h2 className="text-xl font-bold mb-4">Total price of products</h2>
            <p className="text-sm text-gray-400 mb-2">
              Number of Products (
              {cartProducts.reduce((acc, item) => acc + item.qty, 0)})
            </p>
            <p className="text-3xl font-bold mb-6">
              <span dangerouslySetInnerHTML={{ __html: currency }} />
              {cartProducts
                .reduce((acc, item) => acc + item.price * item.qty, 0)
                .toFixed(2)}{" "}
            </p>
            <button
              onClick={handleCheckout}
              className="w-full bg-[#ffd700] text-black py-3 rounded-lg font-bold text-lg mb-4 hover:bg-[#e5c100] transition duration-300"
            >
              Checkout
            </button>
            <button
              onClick={() => navigate("/swiss-bar")}
              className="w-full border border-[#ffd700] text-[#ffd700] py-3 rounded-lg font-bold text-lg hover:bg-[#ffd700] hover:text-black transition duration-300"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItems;
